import {
  homeworkOverdue,
  monthlyState,
  periodTitle,
  type StudentLastHomework,
  type StudentPayment,
} from "@edu/shared";
import { agoLabel, dueLabel, type WhenLabel } from "./when.ts";

/**
 * Помесячная оплата одной фразой.
 *
 * Здесь по той же причине, по которой здесь `homeworkFact`: читателей двое, и
 * оба про одного ученика. Плитка сводки и подпись у заголовка панели учёта
 * обязаны говорить одно и то же, а два текста про одно однажды разойдутся.
 *
 * Правило («какой месяц считается должным») живёт не здесь, а в `monthlyState`
 * в `@edu/shared`: там оно чистое и покрыто тестами, а тут только слова.
 *
 * ⚠️ Слова «просрочен» нет и не будет. Просрочка требует срока, а срока мы не
 * знаем: какого числа семья договорилась переводить деньги, продукту
 * неизвестно, и выдумать его значит выдумать долг у того, кто просто не
 * отмечает.
 */
export function monthlySummary(payment: StudentPayment): string {
  /*
    ⚠️ Месяц «сейчас» берётся ИЗ ОТВЕТА, а не считается здесь по зоне
    смотрящего. Чипы сервер строит в зоне СЕРИИ, а у пары без расписания это
    запасной UTC, и своим расчётом клиент получал бы другой месяц: занятие
    ложилось под чип «авг» при подчёркнутом «сен». Правило живёт в одном месте,
    и это место сервер.
  */
  if (payment.currentPeriod === null) return "оплат не отмечено";
  const state = monthlyState(payment.months, payment.currentPeriod);
  if (state.kind === "unpaid") return `не оплачен ${periodTitle(state.period)}`;
  if (state.kind === "paid_through") return `оплачен по ${periodTitle(state.period)}`;
  return "оплат не отмечено";
}

/**
 * Показывать ли плитку оплат приглушённой.
 *
 * ⚠️ Гасится ТОЛЬКО отсутствие данных, и спрашивается это состоянием, а не
 * массивом. Проверка «ни одной отметки нет» гасила и `unpaid`, то есть самое
 * обычное состояние новой пары («не оплачен август») выглядело как «данных
 * нет».
 */
export function monthlyQuiet(payment: StudentPayment): boolean {
  if (payment.currentPeriod === null) return true;
  return monthlyState(payment.months, payment.currentPeriod).kind === "empty";
}

/**
 * Что происходит с последней домашней работой ученика.
 *
 * Решение живёт здесь одно на двоих: колонка «Домашка» в списке учеников и
 * плитка «Домашка» в карточке. Разъехавшись, они дали бы список, где работа
 * просрочена, и карточку того же ученика, где она «в работе»: даты одни, а
 * границы разные.
 *
 * Вид при этом у читателей разный, и это нормально. В списке есть колонка, там
 * состояние стоит чипом, а срок отдельным моноширинным ярлыком рядом. В
 * карточке плитка одна, и обе половины склеиваются в строку.
 */
export interface HomeworkFact {
  kind: "none" | "done" | "overdue" | "open";
  /** Состояние словом: «не задавали», «сдана», «просрочена», «не открыта». */
  label: string;
  /**
   * Ярлык времени: срок у несданной работы, дата выдачи у сданной. У `none`
   * пустой, потому что говорить о времени работы, которой не было, нечего.
   */
  when: WhenLabel;
  /** Название работы: уезжает в подсказку, в строку не помещается. */
  title: string | null;
}

const NO_WHEN: WhenLabel = { text: "", tone: "none", title: "" };

export function homeworkFact(
  hw: StudentLastHomework | null | undefined,
  now: number,
): HomeworkFact {
  // Отсутствие поля и `null` это одна и та же новость. Поля нет у старого
  // сервера (web едет вперёд API), `null` означает «работ ещё не было»
  if (hw === undefined || hw === null) {
    return { kind: "none", label: "не задавали", when: NO_WHEN, title: null };
  }

  const dueAt = hw.dueAt ?? null;

  if (hw.state === "done") {
    // Своей даты сдачи в сводке нет, показываем дату выдачи: она отвечает на
    // «давно ли это было», ради чего строку и читают
    return { kind: "done", label: "сдана", when: agoLabel(hw.createdAt, now), title: hw.title };
  }

  // ⚠️ Просрочку считает общий `homeworkOverdue`, тот же предикат, что у
  // сервера в `?state=overdue`. Своё сравнение дат здесь означало бы третью
  // границу просрочки в проекте
  const overdue = homeworkOverdue({ dueAt, state: hw.state, now });
  const when = dueLabel(dueAt, now);
  if (overdue) return { kind: "overdue", label: "просрочена", when, title: hw.title };

  return {
    kind: "open",
    label: hw.state === "not_opened" ? "не открыта" : "в работе",
    when,
    title: hw.title,
  };
}

/**
 * Домашка одной строкой: для плитки в карточке, где отдельного места под срок
 * нет. У просроченной работы срок и есть состояние («просрочено 3 дня»), поэтому
 * слово «просрочена» перед ним не повторяется.
 */
export function homeworkFactText(fact: HomeworkFact): string {
  if (fact.kind === "none") return fact.label;
  if (fact.kind === "overdue") return fact.when.text;
  return fact.when.text === "" ? fact.label : `${fact.label}, ${fact.when.text}`;
}
